import { gql } from "@apollo/client";

export const BILLING_PLAN_FRAGMENT = gql`
    fragment BillingPlan on BillingPlanObject {
        name
        description
        stripeProductId
        paymentOptions {
            flatAmount
            unitAmount
            unitsIncluded
            frequency
            stripePriceId
        }
    }
`;

export const BUDGET_FRAGMENT = gql`
    fragment Budget on BudgetObject {
        id
        identifier
        name
        amountCurrency
        amountIncome
        startsOn
        status
        entity {
            id
            identifier
            currency
        }
    }
`;

export const BUDGET_LINE_FRAGMENT = gql`
    fragment BudgetLine on BudgetLineObject {
        id
        identifier
        name
        kind
        bucket
        isSpread
        amountPlanned
        amountCurrency
        amountGoal
        goalBy
        goalMode
        isPaused
        index
        amountOpening
        chartedAccount {
            id
            identifier
            name
            path
            parent {
                id
                name
            }
        }
        budget {
            id
            identifier
        }
        entity {
            id
            identifier
            currency
        }
    }
`;

export const BUDGET_MONTH_FRAGMENT = gql`
    fragment BudgetMonth on BudgetMonthObject {
        month
        daysLeft
        leftover {
            month
            amount
        }
        amountSpread
        shareOfMonth
        pace
        buckets {
            bucket
            amountPlanned
            amountSpent
            pace
            lines {
                line {
                    id
                }
            }
        }
        attention {
            reason
            amount
            line {
                line {
                    id
                }
            }
            fromLine {
                amountLeft
                line {
                    id
                }
            }
            amountFrom
        }
        amountIncomePlanned
        amountIncome
        amountBills
        amountSaving
        amountEverydayPlanned
        amountSpent
        waitingCount
        amountWaiting
        amountLeft
        amountLeftPlanned
        amountPerDay
        uncountedCurrencies
        lines {
            amountPlanned
            amountSpent
            amountDue
            amountLeft
            amountSaved
            amountOwed
            amountNeeded
            readyOn
            isAffordable
            coveredOn
            bills {
                id
                identifier
                name
                amountTotal
                amountDue
                dateDue
            }
            line {
                ...BudgetLine
            }
        }
    }
`;

export const CASH_FLOW_STATEMENT_FRAGMENT = gql`
    fragment CashFlowStatement on CashFlowStatementObject {
        operatingActivities(endDate: $endDate, mode: $mode) {
            ...Metric
        }
        investingActivities(endDate: $endDate, mode: $mode) {
            ...Metric
        }
        financingActivities(endDate: $endDate, mode: $mode) {
            ...Metric
        }
        totalCashFlows(endDate: $endDate, mode: $mode) {
            ...Metric
        }
        operatingActivitiesChart(
            startDate: $startDate,
            endDate: $endDate,
            mode: $mode,
            frequency: $frequency
        ) {
            ...ChartData
        }
        investingActivitiesChart(
            startDate: $startDate,
            endDate: $endDate,
            mode: $mode,
            frequency: $frequency
        ) {
            ...ChartData
        }
        financingActivitiesChart(
            startDate: $startDate,
            endDate: $endDate,
            mode: $mode,
            frequency: $frequency
        ) {
            ...ChartData
        }
        totalCashFlowsChart(
            startDate: $startDate,
            endDate: $endDate,
            mode: $mode,
            frequency: $frequency
        ) {
            ...ChartData
        }
        spendingByCategoryChart(
            startDate: $startDate,
            endDate: $endDate
        ) {
            ...ChartData
        }
    }
`;

export const CATEGORY_FRAGMENT = gql`
    fragment Category on ChartedAccountObject {
        id
        identifier
        name
        color
        isCategory
        parent {
            id
            identifier
            name
        }
        entity {
            id
            identifier
        }
    }
`;

export const CHARTED_ACCOUNT_FRAGMENT = gql`
    fragment ChartedAccount on ChartedAccountObject {
        id
        identifier
        createdAt
        description
        name
        path
        balance(startDate: $startDate, endDate: $endDate) {
            startDate
            endDate
            startingValue
            endingValue
            delta
        }
        children {
            id
            identifier
            name
        }
        parent {
            id
            identifier
            name
            type
            subtype
            path
        }
        entity {
            id
            identifier
            name
        }
        type
        subtype
        isActive
        isPayable
        chartedAccountType: type
        tags {
            ...Tag
        }
        categories {
            ...Category
        }
    }
`;

export const CHART_DATA_FRAGMENT = gql`
    fragment ChartData on ChartData {
        title
        series
        labelFormat
        valueFormat
        labelAxis
        valueAxis
        points {
            label
            values {
                key
                value
            }
        }
    }
`;

export const CONTACT_FRAGMENT = gql`
    fragment Contact on ContactObject {
        id
        identifier
        name
        organization {
            id
            identifier
            name
            domain
            logoUrl
        }
        emailAddress
        phoneNumber
        shippingAddress {
            addressLine1
            addressLine2
            city
            region
            postalCode
            country
        }
        billingAddress {
            addressLine1
            addressLine2
            city
            region
            postalCode
            country
        }
        entity {
            id
            identifier
            name
        }
        type
        tags {
            ...Tag
        }
    }
`;

export const DOCUMENT_FRAGMENT = gql`
    fragment Document on DocumentObject {
        id
        identifier
        __typename
        ...PrintedDocument
        amountTax
        amountTotal
        # In another currency than its business's: the rate to the business's,
        # whether it is the official one - an estimate - and its total there.
        amountToBusinessFx
        isAmountToBusinessFxOfficial
        amountTotalBusiness
        contact {
            id
            identifier
            name
            emailAddress
            organization {
                id
                identifier
                name
                logoUrl
            }
        }
        createdAt
        invoiceState: invoiceState
        entity {
            id
            identifier
            name
            currency
        }
        fileProcessingState
        file {
            name
            url
        }
        financialAccount {
            id
            identifier
            name
        }
        template {
            id
            identifier
            name
        }
        fromEmailAddress
        incomingLinks {
            id
            identifier
            source {
                id
                identifier
                name
            }
        }
        isPublic
        outgoingLinks {
            id
            identifier
            target {
                id
                identifier
                name
            }
        }
        pictures {
            ...File
        }
        updatedAt
}
`;

export const DOCUMENT_TEMPLATE_FRAGMENT = gql`
    fragment DocumentTemplate on DocumentTemplateObject {
        id
        identifier
        name
        layout
        font
        color
        paymentInstructions
        footer
        isDefault
        createdAt
        entity {
            id
            identifier
            name
        }
    }
`;

export const ENTITY_FRAGMENT = gql`
    fragment Entity on EntityObject {
        id
        identifier
        name
        entityType
        fiscalYearStartMonth
        currency
        inboundForwardingAddress
        createdAt
        updatedAt
        rootChartedAccount {
            id
            identifier
            name
            path
        }
        profile {
            id
            identifier
            description
            industry
            businessNumber
        }
        # Member rows with status, invite date, groups and display name are the
        # settings members panel's payload — see ENTITY_MEMBERS_QUERY.
        accounts {
            id
            identifier
            isOwner
        }
        invitedMembers {
            id
            identifier
            invitedAt
            memberEmailAddress
            memberDisplayName
        }
        settings {
            id
            identifier
            billingPlan {
                id
                identifier
                stripeCustomerId
                stripePaymentMethodId
                stripePriceId
                stripeSubscriptionId
                stripeSubscriptionStatus
            }
        }
        integrations {
            stripe {
                isActive
            }
        }
        tagGroups {
            ...TagGroup
        }
        categories {
            ...Category
        }
        isUserEntity
        isAccountingFirm
    }
`;

export const ENTITY_PROFILE_FRAGMENT = gql`
    fragment EntityProfile on EntityProfileObject {
        id
        identifier
        description
        industry
        businessNumber
    }
`;

export const ENTITY_SETTINGS_FRAGMENT = gql`
    fragment EntitySettings on EntitySettingsObject {
        id
        identifier
        documentNameFormat
        billingPlan {
            id
            identifier
            stripeCustomerId
            stripePaymentMethodId
            stripePriceId
            stripeSubscriptionId
            stripeSubscriptionStatus
        }
    }
`;

export const ERRORS_FRAGMENT = gql`
    fragment Errors on ErrorsObject {
        authenticationError {
            message
            helpMessage
        }
        authorizationError {
            message
            helpMessage
        }
        rateLimitError {
            message
            helpMessage
        }
        notFoundError {
            message
            helpMessage
        }
        subscriptionError {
            message
            helpMessage
        }
        genericErrors {
            message
            helpMessage
        }
        fieldErrors {
            field
            message
            helpMessage
        }
    }
`;

export const EXECUTION_FRAGMENT = gql`
    fragment Execution on ExecutionObject {
        id
        identifier
        createdAt
        updatedAt
        source
        status
        triggerType
        startedAt
        completedAt
        error
        seedContext
        rule {
            id
            identifier
            name
            triggerType
            triggerObjectType
            triggerOperation
            scheduleFrequency
        }
        thread {
            id
            identifier
            name
        }
    }
`;

export const EXECUTION_LIST_FRAGMENT = gql`
    fragment ExecutionListItem on ExecutionObject {
        id
        identifier
        createdAt
        updatedAt
        source
        status
        triggerType
        startedAt
        completedAt
        error
        seedContext
        rule {
            id
            identifier
            name
            triggerType
            triggerObjectType
            triggerOperation
            scheduleFrequency
        }
        thread {
            id
            identifier
            name
            entity {
                id
                identifier
                name
            }
        }
    }
`;

export const EXTERNAL_GRANT_DELEGATION_FRAGMENT = gql`
    fragment ExternalGrantDelegation on ExternalGrantDelegationObject {
        id
        identifier
        createdAt
        updatedAt
        accountIdentifier
        memberName
        memberEmailAddress
        roleIdentifier
        roleName
        rolePermissions
        memberPermissions
    }
`;

export const EXTERNAL_GRANT_FRAGMENT = gql`
    fragment ExternalGrant on ExternalGrantObject {
        id
        identifier
        createdAt
        updatedAt
        grantStatus: status
        invitedAt
        isGiven
        invitedEmailAddress: emailAddress
        entityIdentifier
        entityName
        # A person's own books, rather than a business's: aliased, as the business's own flag reads nullable.
        isOwnBooks: isUserEntity
        sharedByIdentifier
        sharedByName
        sharedByEmailAddress
        granteeName
        granteeIdentifier
        roleName
        roleIdentifier
        rolePermissions
    }
`;

export const FILE_FRAGMENT = gql`
    fragment File on FileObject {
        id
        identifier
        createdAt
        name
        fileType: type
        url
        size
    }
`;

export const FINANCIAL_ACCOUNT_FRAGMENT = gql`
    fragment FinancialAccount on FinancialAccountObject {
        id
        identifier
        routingNumber
        accountType
        type
        subtype
        mask
        name
        officialName
        transitNumber
        institutionNumber
        accountNumber
        integrationProvider
        plaidBankAccountId
        syncState
        syncCompletedAt
        openingBalance
        openingBalanceDate
        financialAccountBalance: balance
        financialAccountChartedAccount: chartedAccount {
            balance {
                endingValue
            }
        }
        financialInstitution {
            id
            identifier
            institutionName
            organization {
                id
                identifier
                name
                logoUrl
            }
            entity {
                id
                identifier
                name
            }
        }
        tags {
            ...Tag
        }
    }
`;

export const FINANCIAL_INSTITUTION_FRAGMENT = gql`
    fragment FinancialInstitution on FinancialInstitutionObject {
        id
        identifier
        institutionName
        organization {
            id
            identifier
            name
            logoUrl
        }
        financialAccounts {
            ... on FinancialAccountObject {
                id
                identifier
                ...FinancialAccount
            }
            ... on StripeFinancialAccountObject {
                id
                identifier
                ...StripeFinancialAccount
            }
        }
    }
`;

export const INTER_ENTITY_TRANSFER_FRAGMENT = gql`
    fragment InterEntityTransfer on InterEntityTransferObject {
        id
        identifier
        createdAt
        updatedAt
        amount
        description
        sourceEntity {
            id
            identifier
            name
        }
        targetEntity {
            id
            identifier
            name
        }
        sourceTransaction {
            id
            identifier
            description
        }
        targetTransaction {
            id
            identifier
            description
        }
    }
`;

export const I_BRANCH_FRAGMENT = gql`
    fragment IBranch on IBranchObject {
        name
        path
        children {
            name
        }
    }
`;

export const JOURNAL_ENTRY_FRAGMENT = gql`
    fragment JournalEntry on JournalEntryObject {
        id
        identifier
        createdAt
        updatedAt
        externalCreatedAt
        externalUpdatedAt
        description
        # Lines are the Journal Entry tab's payload — JOURNAL_ENTRY_LINES_QUERY.
        entity {
            id
            identifier
            name
        }
        subtotal
        total
        journalEntryState: state
        tags {
            ...Tag
        }
    }
`;

export const MESSAGE_CONTEXT_FRAGMENT = gql`
    fragment MessageContext on MessageContextObject {
        node {
            id
            ...on FinancialAccountObject {
                ...FinancialAccount
            }
            ...on StripeFinancialAccountObject {
                ...StripeFinancialAccount
            }
            ...on ChartedAccountObject {
                ...ChartedAccount
            }
            ...on ContactObject {
                ...Contact
            }
            ...on DocumentObject {
                ...Document
            }
            ...on EntityObject {
                ...Entity
            }
            ...on JournalEntryObject {
                ...JournalEntry
            }
            ...on NotificationObject {
                ...Notification
            }
            ...on PublicContactObject {
                ...PublicContact
            }
            ...on PublicDocumentObject {
                ...PublicDocument
            }
            ...on PublicTransactionObject {
                ...PublicTransaction
            }
            ...on TagObject {
                ...Tag
            }
            ...on TagGroupObject {
                ...TagGroup
            }
            ...on TaxObject {
                ...Tax
            }
            ...on TaxRateObject {
                ...TaxRate
            }
            ...on TransactionObject {
                ...Transaction
            }
            ...on UserObject {
                ...User
            }
            ...on UserProfileObject {
                ...UserProfile
            }
            ...on UserSettingsObject {
                ...UserSettings
            }
            ...on RuleDefinitionObject {
                id
                identifier
                name
            }
            ...on RuleSuggestionObject {
                id
                identifier
                # The payment it is on: one worked out again in the background is read again where it is shown.
                transaction {
                    id
                    identifier
                }
            }
            ...on RuleDefinitionSuggestionMatchDocumentObject {
                id
                identifier
            }
            ...on RuleDefinitionSuggestionCategorizeObject {
                id
                identifier
            }
            ...on RuleDefinitionSuggestionDuplicateObject {
                id
                identifier
            }
            ...on RuleDefinitionSuggestionSplitObject {
                id
                identifier
            }
            ...on RuleDefinitionSuggestionTransferObject {
                id
                identifier
            }
        }
        isPresented
    }
`;

export const METRIC_FRAGMENT = gql`
    fragment Metric on MetricObject {
        period {
            startDate
            endDate
            inflowValue
            outflowValue
            netValue
        }
        cumulative {
            startDate
            endDate
            startingValue
            endingValue
            delta
        }
    }
`;

export const NOTIFICATION_FRAGMENT = gql`
    fragment Notification on NotificationObject {
        
        id
        identifier
        createdAt
        updatedAt
        message
        isRead
        isArchived
        isDeleted
        entity {
            id
            identifier
            isUserEntity
            name
        }
        entityDocuments: documents {
            id
            identifier
            createdAt
            name
            description
            reference
        }
        entityTransactions: transactions {
            id
            identifier
            createdAt
            description
        }
        entityChartedAccounts: chartedAccounts {
            id
            identifier
            createdAt
            name
            path
            fullPath
        }
        entityEntities: entities {
            id
            identifier
            createdAt
            name
        }
        entityJournalEntries: journalEntries {
            id
            identifier
            createdAt
            description
        }
        entityAccounts: accounts {
            id
            identifier
            createdAt
        }

    }
`;

export const ORGANIZATION_FRAGMENT = gql`
    fragment Organization on OrganizationObject {
        id
        identifier
        name
        domain
        logoUrl
        createdAt
        entity {
            id
            identifier
            name
        }
    }
`;

export const PENDING_CHANGE_FRAGMENT = gql`
    fragment PendingChange on PendingChangeObject {
        identifier
        changeType
        modelName
        status
        objectIdentifier
        resultingData
        parent {
            identifier
        }
        children {
            identifier
            changeType
            modelName
            status
        }
        deltas {
            fieldName
            originalValue
            currentValue
            fieldType
        }
    }
`;

export const PRINTED_DOCUMENT_FRAGMENT = gql`
    fragment PrintedDocument on IDocument {
        amountCurrency
        amountDiscounted
        amountDue
        dateDue
        dateIssued
        description
        documentType: type
        name
        payableFrom
        payableTo
        paymentMethods
        reference
    }
`;

export const PRINTED_LINE_ITEM_FRAGMENT = gql`
    fragment PrintedLineItem on ILineItem {
        description
        quantity
        amount
        amountSubtotal
        amountTax
        tax {
            name
            totalRate
        }
    }
`;

export const PUBLIC_CONTACT_FRAGMENT = gql`
    fragment PublicContact on PublicContactObject {
        id
        identifier
        name
        organizationName
        emailAddress
        phoneNumber
        shippingAddress {
            addressLine1
            addressLine2
            city
            region
            postalCode
            country
        }
        billingAddress {
            addressLine1
            addressLine2
            city
            region
            postalCode
            country
        }
        type
    }
`;

export const PUBLIC_DOCUMENT_FRAGMENT = gql`
    fragment PublicDocument on PublicDocumentObject {
        __typename
        id
        identifier
        ...PrintedDocument
        amountPaid
        amountRefunded
        amountTax
        amountTotal
        canAcceptPayments
        contact {
            id
            identifier
            name
            emailAddress
            organizationName
        }
        createdAt
        dateCancelled
        datePaid
        invoiceState: invoiceState
        entity {
            id
            identifier
            name
        }
        paymentMethod
        paymentReference
        paymentTerms
        refundReason
        shipFrom
        shipTo
        template {
            id
            layout
            font
            color
            paymentInstructions
            footer
        }
        updatedAt
    }
`;

export const PUBLIC_TRANSACTION_FRAGMENT = gql`
    fragment PublicTransaction on PublicTransactionObject {
        id
        identifier
        createdAt
        updatedAt
        amount
        description
        entity {
            id
            identifier
            name
        }
    }
`;

export const REPORT_DEFINITION_FRAGMENT = gql`
    fragment ReportDefinition on ReportDefinitionObject {
        version
        groups {
            id
            name
            layouts {

                xs { cards { cardId colSpan visible colStart colEnd rowStart rowEnd } }
                sm { cards { cardId colSpan visible colStart colEnd rowStart rowEnd } }
                md { cards { cardId colSpan visible colStart colEnd rowStart rowEnd } }
                lg { cards { cardId colSpan visible colStart colEnd rowStart rowEnd } }
                xl { cards { cardId colSpan visible colStart colEnd rowStart rowEnd } }

            }
        }
        layouts {

                xs { cards { cardId colSpan visible colStart colEnd rowStart rowEnd } }
                sm { cards { cardId colSpan visible colStart colEnd rowStart rowEnd } }
                md { cards { cardId colSpan visible colStart colEnd rowStart rowEnd } }
                lg { cards { cardId colSpan visible colStart colEnd rowStart rowEnd } }
                xl { cards { cardId colSpan visible colStart colEnd rowStart rowEnd } }

        }
        widgets {
            id
            title
            groupId
            source
            presentation
            spec {
            title
            groupBy { field interval }
            aggregate { op field }
            filters { field operator value }
            cumulative
            dateField
            columns
            orderBy
            limit
            where {
                field operator value isNegated connector
                terms {
                    field operator value isNegated connector
                    terms {
                        field operator value isNegated connector
                        terms { field operator value isNegated connector }
                    }
                }
            }
            }
            entityScope { mode entityIdentifiers }
            defaultFilters { key type operator value isLocked }
        }
    }
`;

export const REPORT_WIDGET_SHAPE_FRAGMENT = gql`
    fragment ReportWidgetShape on ReportWidgetData {
        chart {
            title
            series
            labelFormat
            valueFormat
            labelAxis
            valueAxis
            points {
                label
                values { key value }
            }
        }
        table {
            title
            columns { key label columnKind typename }
            rows {
                rowKey
                label
                rowKind
                cells { columnKey amount formattedAmount entityName identifier }
                children {
                    rowKey
                    label
                    rowKind
                    cells { columnKey amount formattedAmount entityName }
                }
            }
        }
        tree {
            columns { key label columnKind }
            roots {
                rowKey
                label
                rowKind
                isNegated
                cells { columnKey amount formattedAmount entityName }
                children {
                    rowKey
                    label
                    rowKind
                    isNegated
                    cells { columnKey amount formattedAmount entityName }
                    children {
                        rowKey
                        label
                        rowKind
                        isNegated
                        cells { columnKey amount formattedAmount entityName }
                    }
                }
            }
        }
        matrix {
            entities { identifier label }
            cells { debtorIdentifier creditorIdentifier amount }
        }
    }
`;

export const ROLE_FRAGMENT = gql`
    fragment Role on RoleObject {
        id
        identifier
        createdAt
        updatedAt
        roleName: name
        isDefault
        isEditable
        permissions
    }
`;

export const RULE_DEFINITION_SUGGESTION_CATEGORIZE_FRAGMENT = gql`
    fragment RuleDefinitionSuggestionCategorize on RuleDefinitionSuggestionCategorizeObject {
        id
        identifier
        name
        description
        type
        rationale
        minConfidence
        summary
        category { id identifier name }
        strategies
        setChartedAccount { id identifier name }
        setTax { id identifier name }
        setContact { id identifier emailAddress organization { id identifier name logoUrl } }
        setTags { id identifier name color }
        pendingChange { identifier changeType }
    }
`;

export const RULE_DEFINITION_SUGGESTION_DUPLICATE_FRAGMENT = gql`
    fragment RuleDefinitionSuggestionDuplicate on RuleDefinitionSuggestionDuplicateObject {
        id
        identifier
        name
        description
        type
        rationale
        minConfidence
        summary
        amountTolerance
        dateWindowDays
        descriptionSimilarityThreshold
        sameFinancialAccount
        requireSameSign
        excludeReconciled
        resolution
        pendingChange { identifier changeType }
    }
`;

export const RULE_DEFINITION_SUGGESTION_MATCH_DOCUMENT_FRAGMENT = gql`
    fragment RuleDefinitionSuggestionMatchDocument on RuleDefinitionSuggestionMatchDocumentObject {
        id
        identifier
        name
        description
        type
        rationale
        minConfidence
        summary
        amountTolerance
        amountToleranceMode
        dateWindowDays
        dateWindowDaysBefore
        dateWindowDaysAfter
        matchDateBasis
        detectFx
        currencyMode
        rateTolerance
        includePaid
        textMatchMode
        similarityThreshold
        automationMode
        maxResults
        maxCombinedDocuments
        documentTypes
        directionMode
        contactFilterMode
        filteredContacts { id identifier name }
        settlementEntities { id identifier name }
        residualRules {
            id
            identifier
            position
            reason
            tolerance
            percentTolerance
            chartedAccount { id identifier name }
        }
        postingMode
        uncodedLinePolicy
        unsummedLinesPolicy
        settlementBases
        undatedDocumentPolicy
        copyLineItemTax
        payableAccount { id identifier name }
        receivableAccount { id identifier name }
        fixedAccount { id identifier name }
        uncodedAccount { id identifier name }
        pendingChange { identifier changeType }
    }
`;

export const RULE_DEFINITION_SUGGESTION_SPLIT_FRAGMENT = gql`
    fragment RuleDefinitionSuggestionSplit on RuleDefinitionSuggestionSplitObject {
        id
        identifier
        name
        description
        type
        rationale
        minConfidence
        summary
        splitMethod
        maxParts
        roundingMode
        requireDocumentMatch
        remainderAccount { id identifier name }
        pendingChange { identifier changeType }
    }
`;

export const RULE_DEFINITION_SUGGESTION_TRANSFER_FRAGMENT = gql`
    fragment RuleDefinitionSuggestionTransfer on RuleDefinitionSuggestionTransferObject {
        id
        identifier
        name
        description
        type
        rationale
        minConfidence
        summary
        clearingAccount { id identifier name }
        amountTolerance
        dateWindowDays
        detectFx
        fxGainLossAccount { id identifier name }
        pendingChange { identifier changeType }
    }
`;

export const RULE_FRAGMENT = gql`
    fragment Rule on RuleDefinitionObject {
        id
        identifier
        name
        description
        isAutoApply
        priority
        triggerType
        triggerObjectType
        triggerOperation
        triggerWatchFields
        scheduleFrequency
        scheduleDayOfMonth
        scheduleWeekday
        scheduleHour
        scheduleMinute
        agentMessage
        entity {
            id
            identifier
            name
        }
        delegations {
            id
            identifier
            entity {
                id
                identifier
                name
            }
        }
        agentContext {
            id
            identifier
            objectIdentifier
        }
        suggestions(includePending: true) {
            ... on RuleDefinitionSuggestionMatchDocumentObject { ...RuleDefinitionSuggestionMatchDocument }
            ... on RuleDefinitionSuggestionCategorizeObject { ...RuleDefinitionSuggestionCategorize }
            ... on RuleDefinitionSuggestionDuplicateObject { ...RuleDefinitionSuggestionDuplicate }
            ... on RuleDefinitionSuggestionSplitObject { ...RuleDefinitionSuggestionSplit }
            ... on RuleDefinitionSuggestionTransferObject { ...RuleDefinitionSuggestionTransfer }
        }
        conditions(includePending: true) {
            id
            identifier
            position
            connector
            isNegated
            field
            operator
            valueType
            value
            pendingChange { identifier changeType }
            children {
                id
                identifier
                position
                connector
                isNegated
                field
                operator
                valueType
                value
                pendingChange { identifier changeType }
                children {
                    id
                    identifier
                    position
                    connector
                    isNegated
                    field
                    operator
                    valueType
                    value
                    pendingChange { identifier changeType }
                }
            }
        }
    }
`;

export const RULE_SUGGESTION_LINE_FRAGMENT = gql`
    fragment RuleSuggestionLine on RuleSuggestionLineObject {
        id
        identifier
        chartedAccount {
            id
            identifier
            name
            path
            fullPath
        }
        catalogueAccountName
        catalogueAccountPath
    }
`;

export const SEARCH_RESULT_FRAGMENT = gql`
    fragment SearchResult on SearchIndexObject {
        node {
            ... on ChartedAccountObject {
                ...ChartedAccount
            }
            ... on ContactObject {
                ...Contact
            }
            ... on DocumentObject {
                ...Document
            }
            ... on TransactionObject {
                ...Transaction
            }
        }
    }
`;

export const STRIPE_FINANCIAL_ACCOUNT_FRAGMENT = gql`
    fragment StripeFinancialAccount on StripeFinancialAccountObject {
        id
        identifier
        name
        mask
        officialName
        type
        subtype
        integrationProvider
        financialInstitution {
            id
            identifier
            institutionName
            entity {
                id
                identifier
                name
            }
        }
        tags {
            ...Tag
        }
    }
`;

export const TAG_FRAGMENT = gql`
    fragment Tag on TagObject {
        id
        identifier
        name
        color
    }
`;

export const TAG_GROUP_FRAGMENT = gql`
    fragment TagGroup on TagGroupObject {
        id
        identifier
        name
        tags {
            ...Tag
        }
    }
`;

export const TAX_FRAGMENT = gql`
    fragment Tax on TaxObject {
        id
        identifier
        name
        description
        totalRate
        ledgerEntity {
            id
            identifier
        }
        rates {
            ...TaxRate
        }
    }
`;

export const TAX_RATE_FRAGMENT = gql`
    fragment TaxRate on TaxRateObject {
        id
        identifier
        name
        description
        rate
        metadata
        chartedAccount {
            id
            identifier
            name
            entity {
                id
                identifier
            }
        }
    }
`;

export const THREAD_FRAGMENT = gql`
    fragment Thread on ThreadObject {
        id
        identifier
        createdAt
        updatedAt
        name
        description
        isRead
        isArchived
        source
        # Whose chat it is: a record of that business needs no badge in its changes tray; its composer says
        # whose it is - a business's, seen by everyone there, or its person's own, private.
        entity {
            id
            identifier
            name
            isUserEntity
        }
        # The firms its business's books are shared with that it is shared with, and the people there: whoever
        # works in the books through a grant reads only the chats shared with them.
        shares {
            id
            identifier
            grantIdentifier
            granteeName
            accountIdentifier
            memberName
        }
        scopedEntities {
            id
            identifier
            name
        }
        # Messages and the pending-session change list are the thread panel's
        # payload — THREAD_WITH_MESSAGES_QUERY loads them by thread id.
        pendingSession {
            identifier
            status
            changeCount
            pendingChangeCount
            hasPendingChanges
        }
    }
`;

export const THREAD_LIST_FRAGMENT = gql`
    fragment ThreadListItem on ThreadObject {
        id
        identifier
        createdAt
        updatedAt
        name
        summary
        lastUserMessagePreview
        isRead
        isArchived
        source
        # Whose chat it is - a business's, or its person's own, private.
        entity {
            id
            identifier
            name
            isUserEntity
        }
        pendingSession {
            identifier
            status
            changeCount
            pendingChangeCount
            hasPendingChanges
            pendingChanges {
                changeType
                status
            }
        }
    }
`;

export const THREAD_MESSAGE_FRAGMENT = gql`
    fragment ThreadMessage on MessageObject {
        id
        identifier
        createdAt
        content {
            id
            identifier
            createdAt
            order
            type
            status
            text
            toolName
            toolInput
            toolOutput
            toolError
            parentIdentifier
            executionMode
            iterationIndex
            execution {
                identifier
                source
                status
            }
            context { id identifier }
            pendingChange {
                identifier
                changeType
                modelName
                status
                objectIdentifier
            }
            nodes {
                id
                identifier
                createdAt
                __typename
                ... on TransactionObject {
                    description
                    amount
                }
                ... on ContactObject {
                    name
                }
                ... on DocumentObject {
                    name
                }
                ... on TagObject {
                    name
                    color
                }
                ... on RuleDefinitionSuggestionMatchDocumentObject {
                    name
                    summary
                }
                ... on RuleDefinitionSuggestionCategorizeObject {
                    name
                    summary
                }
                ... on RuleDefinitionSuggestionDuplicateObject {
                    name
                    summary
                }
                ... on RuleDefinitionSuggestionSplitObject {
                    name
                    summary
                }
                ... on RuleDefinitionSuggestionTransferObject {
                    name
                    summary
                }
            }
        }
        context { id identifier }
        # The files the user attached to the message. Empty on an agent's message.
        files { id identifier name }
        # What the user sent the message about, named so the thread still says
        # what "this" was when read again later. Empty on an agent's message.
        references {
            id
            identifier
            node {
                id
                __typename
                ... on TransactionObject { identifier description }
                ... on ContactObject { identifier name }
                ... on DocumentObject { identifier name }
                ... on TagObject { identifier name }
                ... on ChartedAccountObject { identifier name }
                ... on FinancialAccountObject { identifier name }
                ... on RuleDefinitionObject { identifier name }
                ... on ReportObject { identifier name }
                ... on JournalEntryObject { identifier description }
                ... on TaxObject { identifier name }
            }
        }
        account {
            id
            identifier
        }
        # The agent turn's own run: the panel reads whether a reply is still
        # coming from here, and a run that stops advancing updatedAt has lost
        # its worker.
        execution {
            identifier
            source
            status
            updatedAt
        }
        timeSeriesData {
            ...ChartData
        }
    }
`;

export const TRANSACTION_FRAGMENT = gql`
    fragment Transaction on TransactionObject {
        id
        identifier
        createdAt
        updatedAt
        effectiveDate
        occurredOn
        amount
        amountCurrency
        # What it was in another currency, as its statement prints it: its
        # Original column.
        amountOriginal
        amountOriginalCurrency
        amountOriginalToAmountFx
        description
        notes
        chartedAccount {
            id
            identifier
            name
        }
        journalEntry {
            id
            identifier
            journalEntryState: state
        }
        entity {
            id
            identifier
            name
            currency
        }
        # The other business it is for - said by its Move, or booked into - and how the two settle it.
        belongsToEntity {
            id
            identifier
            name
        }
        settlementTreatment
        # What the queue reads of a bank payment as it changes: at work, booked, held.
        isProcessing
        isBooked
        triageHeldAt
        triageHoldReason
        triageHoldUntil
        triageHeldForViewer
        cameFromEntity {
            id
            identifier
            name
        }
        tags {
            ...Tag
        }
        categoryObj {
            ...Category
        }
        financialAccount {
            id
            identifier
            name
            accountNumber
        }
        paymentFor {
            id
            identifier
            name
            type
        }
        lineItemOn {
            id
            identifier
            name
            type
        }
        pendingChange {
            changeType
            status
            deltas {
                fieldName
            }
        }
    }
`;

export const TRIAGE_FRAGMENT = gql`
    fragment Triage on TransactionObject {
        id
        identifier
        createdAt
        updatedAt
        metadata
        effectiveDate
        amount
        # What it was paid in, where its bank account may say none.
        amountCurrency
        description
        triageHeldAt
        triageHoldReason
        triageHeldForViewer
        isProcessing
        isBooked
        triageHoldReason
        triageHoldUntil
        # Its Similar's count: the same field its card in the queue says after its payee.
        payeePaymentCount
        entity {
            id
            identifier
            name
            # What its books are in: what a payment is in where nothing more particular says.
            currency
        }
        # Suggestions are the detail panel's payload — TRIAGE_DETAIL_QUERY
        # loads them by triage id; the list row keeps TriageListItem stubs.
        categoryObj {
            id
            identifier
            name
            color
        }
        tags {
            id
            identifier
            name
            color
        }
        movedToTransaction {
            id
            identifier
            tags {
                id
                identifier
                name
                color
            }
            paymentFor {
                id
                identifier
                name
            }
        }
        chartedAccount {
            id
            identifier
            name
        }
        # The other business it is for - said by its Move, or booked into - and how the two settle it.
        belongsToEntity {
            id
            identifier
            name
        }
        settlementTreatment
        financialAccount {
            id
            identifier
            name
            accountNumber
            currency
            chartedAccount {
                id
                identifier
                name
                path
                fullPath
            }
        }
        paymentFor {
            id
            identifier
            name
            type
        }
        pendingChange {
            changeType
            status
            deltas {
                fieldName
            }
        }
    }
`;

export const TRIAGE_LIST_ITEM_FRAGMENT = gql`
    fragment TriageListItem on TransactionObject {
        id
        identifier
        description
        # How many of its payee's other payments are as booked as it: "(+ 3 similar)" after its payee, and
        # its Similar's count - the one field both read.
        payeePaymentCount
        amount
        effectiveDate
        triageHeldAt
        triageHoldReason
        triageHeldForViewer
        isProcessing
        # Booked already: listed only among the payments missing a document.
        isBooked
        entity {
            id
            identifier
            name
        }
        financialAccount {
            id
            identifier
            name
            chartedAccount {
                id
                identifier
                name
                path
                fullPath
            }
        }
        chartedAccount {
            id
            identifier
            name
        }
        # The other business it is for - said by its Move, or booked into - and how the two settle it: its row
        # shows that business's half, saying so.
        belongsToEntity {
            id
            identifier
            name
        }
        settlementTreatment
        # Its category, on a chip of its own at the card's foot.
        categoryObj {
            id
            identifier
            name
            color
        }
        # The tags it already has: carried into what books it, not set from the row.
        tags {
            id
            identifier
            name
            color
        }
        # The documents it pays, booked: what its row says it has - or, booked into another business, what its
        # row there pays.
        paymentFor {
            id
            identifier
            name
        }
        # Booked into another business, its row there: its tags are that business's, set there.
        movedToTransaction {
            id
            identifier
            tags {
                id
                identifier
                name
                color
            }
            paymentFor {
                id
                identifier
                name
            }
        }
        # The documents for it that came by email, for the row's mark: who sent
        # each, how it came - forwarded, or found in a connected mailbox - and
        # whether it still waits in review in its chat.
        emailedDocuments {
            id
            identifier
            fromEmailAddress
            externalProvider
            pendingChange {
                identifier
            }
            # Whose it is: said on the row where it is another business's.
            entity {
                id
                identifier
                name
            }
        }
        # What each suggestion is, for the row's marks: whether any is a
        # document, how it would book the payment - the account it posts to -
        # how sure it is, whether it is the AI's, and which other business
        # it is for.
        suggestions {
            id
            identifier
            status
            job
            source
            confidence
            recommendedTreatment
            rule {
                id
                identifier
                name
            }
            # The documents it offers, for the document chip: the first's name, and the logo of whose it is.
            ruleSuggestionDocumentOptions {
                id
                identifier
                document {
                    id
                    identifier
                    name
                    contact {
                        id
                        identifier
                        organization {
                            id
                            identifier
                            name
                            logoUrl
                        }
                    }
                }
            }
            # By their ids, so they are the lines the payment open has - one made
            # to post to an account since, said so in both.
            lines {
                id
                identifier
                amount
                catalogueAccountName
                entity {
                    id
                    identifier
                    name
                }
                chartedAccount {
                    id
                    identifier
                    name
                }
            }
        }
    }
`;

export const TRIAGE_SUGGESTION_FRAGMENT = gql`
    fragment TriageSuggestion on RuleSuggestionObject {
        id
        identifier
        createdAt
        updatedAt
        job
        status
        confidence
        rationale
        selectionRationale
        source
        metadata
        lines {
            id
            identifier
            amount
            amountTax
            tax {
                id
                identifier
                name
            }
            description
            chartedAccount {
                id
                identifier
                name
                path
                fullPath
            }
            catalogueAccountName
            catalogueAccountPath
            entity {
                id
                identifier
                name
            }
        }
        rule {
            id
            identifier
            name
            metadata
            triggerType
            triggerObjectType
            triggerOperation
            scheduleFrequency
            conditions(includePending: true) {

            connector
            isNegated
            field
            operator
            valueType
            value
            children {
                connector
                isNegated
                field
                operator
                valueType
                value
                children {
                    connector
                    isNegated
                    field
                    operator
                    valueType
                    value
                }
            }

            }
        }
        matchDocumentDefinition {
            ...RuleDefinitionSuggestionMatchDocument
        }
        entity {
            id
            identifier
            name
        }
        transaction {
            id
            identifier
        }
        newCategoryName
        ruleSuggestionDocumentOptions {
            id
            identifier
            confidence
            rank
            rationale
            # A unit of its document's currency in the payment's, where they differ.
            exchangeRate
            exchangeRateSource
            document {
                id
                identifier
                name
                description
                reference
                amountTotal
                amountDue
                amountTax
                amountCurrency
                dateIssued
                dateDue
                createdAt
                invoiceState: invoiceState
                documentType: type
                # The payments it is paid by already: one other than this payment is a match booking it would
                # keep beside its own, or replace.
                paymentShares {
                    amountTransaction
                    amountTransactionCurrency
                    amountDocument
                    amountDocumentCurrency
                    amountTransactionToDocumentFx
                    transaction {
                        id
                        identifier
                        description
                        effectiveDate
                        amount
                        paymentFor {
                            id
                            identifier
                            name
                        }
                        entity {
                            id
                            identifier
                            name
                        }
                        financialAccount {
                            id
                            identifier
                            name
                        }
                    }
                }
                file {
                    name
                }
                contact {
                    id
                    identifier
                    emailAddress
                    organization {
                        id
                        identifier
                        name
                        logoUrl
                    }
                }
                entity {
                    id
                    identifier
                    name
                }
            }
        }
        ruleSuggestionChartedAccountOption {
            id
            identifier
            confidence
            rank
            rationale
            chartedAccount {
                id
                identifier
                name
                path
                fullPath
            }
            category {
                id
                identifier
                name
                color
                # The account it narrows.
                parent {
                    id
                    identifier
                    name
                    path
                    fullPath
                }
            }
        }
        ruleSuggestionTransactionOptions {
            id
            identifier
            confidence
            rank
            rationale
            transaction {
                id
                identifier
                description
                amount
                effectiveDate
                externalCreatedAt
                # Whose and where, over its card: its business and its bank account.
                entity {
                    id
                    identifier
                    name
                }
                financialAccount {
                    id
                    identifier
                    name
                }
            }
        }
        ruleSuggestionTagOptions {
            id
            identifier
            confidence
            rank
            rationale
            tag {
                id
                identifier
                name
                color
            }
        }
    }
`;

export const USER_FRAGMENT = gql`
    fragment User on UserObject {
        id
        identifier
        createdAt
        updatedAt
        userEmailAddress: emailAddress
        accounts {
            id
            identifier
            isEntityAccount
            isUserAccount
            entity {
                ...Entity
            }
        }
        settings {
            id
            identifier
        }
        profile {
            id
            identifier
            firstName
            lastName
            phone
        }
    }
`;

export const USER_PROFILE_FRAGMENT = gql`
    fragment UserProfile on UserProfileObject {
        id
        identifier
        name
        firstName
        lastName
        phone
        entity {
            id
            identifier
        }
    }
`;

export const USER_SETTINGS_FRAGMENT = gql`
    fragment UserSettings on UserSettingsObject {
        id
        identifier
        createdAt
        updatedAt
    }
`;

export const VIEW_OBJECT_FRAGMENT = gql`
    fragment ViewLayout on ReportObject {
        id
        identifier
        name
        entities {
            id
            identifier
            name
        }
        definition {
            ...ReportDefinition
        }
        entity {
            id
            identifier
            name
        }
        linkSharingEnabled
        shareToken
        shareEmails
        shareContacts {
            id
            identifier
            name
            emailAddress
            organization {
                id
                identifier
                name
                logoUrl
            }
        }
        createdAt
        updatedAt
    }
`;

export const OBJECT_FRAGMENT = gql`
	fragment Object on IObject {
		...on BudgetObject {
			...Budget
		}
		...on BudgetLineObject {
			...BudgetLine
		}
		...on ChartedAccountObject {
			...ChartedAccount
		}
		...on ContactObject {
			...Contact
		}
		...on DocumentObject {
			...Document
		}
		...on DocumentTemplateObject {
			...DocumentTemplate
		}
		...on EntityObject {
			...Entity
		}
		...on EntityProfileObject {
			...EntityProfile
		}
		...on EntitySettingsObject {
			...EntitySettings
		}
		...on ExecutionObject {
			...Execution
		}
		...on ExternalGrantDelegationObject {
			...ExternalGrantDelegation
		}
		...on ExternalGrantObject {
			...ExternalGrant
		}
		...on FileObject {
			...File
		}
		...on FinancialAccountObject {
			...FinancialAccount
		}
		...on FinancialInstitutionObject {
			...FinancialInstitution
		}
		...on InterEntityTransferObject {
			...InterEntityTransfer
		}
		...on JournalEntryObject {
			...JournalEntry
		}
		...on MessageContextObject {
			...MessageContext
		}
		...on NotificationObject {
			...Notification
		}
		...on OrganizationObject {
			...Organization
		}
		...on PublicContactObject {
			...PublicContact
		}
		...on PublicDocumentObject {
			...PublicDocument
		}
		...on PublicTransactionObject {
			...PublicTransaction
		}
		...on RoleObject {
			...Role
		}
		...on RuleSuggestionLineObject {
			...RuleSuggestionLine
		}
		...on StripeFinancialAccountObject {
			...StripeFinancialAccount
		}
		...on TagObject {
			...Tag
		}
		...on TagGroupObject {
			...TagGroup
		}
		...on TaxObject {
			...Tax
		}
		...on TaxRateObject {
			...TaxRate
		}
		...on ThreadObject {
			...Thread
		}
		...on TransactionObject {
			...Transaction
		}
		...on UserObject {
			...User
		}
		...on UserProfileObject {
			...UserProfile
		}
		...on UserSettingsObject {
			...UserSettings
		}
		...on ReportObject {
			...ViewLayout
		}
	}
`;

const Fragments = [
  BILLING_PLAN_FRAGMENT,
  BUDGET_FRAGMENT,
  BUDGET_LINE_FRAGMENT,
  BUDGET_MONTH_FRAGMENT,
  CASH_FLOW_STATEMENT_FRAGMENT,
  CATEGORY_FRAGMENT,
  CHARTED_ACCOUNT_FRAGMENT,
  CHART_DATA_FRAGMENT,
  CONTACT_FRAGMENT,
  DOCUMENT_FRAGMENT,
  DOCUMENT_TEMPLATE_FRAGMENT,
  ENTITY_FRAGMENT,
  ENTITY_PROFILE_FRAGMENT,
  ENTITY_SETTINGS_FRAGMENT,
  ERRORS_FRAGMENT,
  EXECUTION_FRAGMENT,
  EXECUTION_LIST_FRAGMENT,
  EXTERNAL_GRANT_DELEGATION_FRAGMENT,
  EXTERNAL_GRANT_FRAGMENT,
  FILE_FRAGMENT,
  FINANCIAL_ACCOUNT_FRAGMENT,
  FINANCIAL_INSTITUTION_FRAGMENT,
  INTER_ENTITY_TRANSFER_FRAGMENT,
  I_BRANCH_FRAGMENT,
  JOURNAL_ENTRY_FRAGMENT,
  MESSAGE_CONTEXT_FRAGMENT,
  METRIC_FRAGMENT,
  NOTIFICATION_FRAGMENT,
  ORGANIZATION_FRAGMENT,
  PENDING_CHANGE_FRAGMENT,
  PRINTED_DOCUMENT_FRAGMENT,
  PRINTED_LINE_ITEM_FRAGMENT,
  PUBLIC_CONTACT_FRAGMENT,
  PUBLIC_DOCUMENT_FRAGMENT,
  PUBLIC_TRANSACTION_FRAGMENT,
  REPORT_DEFINITION_FRAGMENT,
  REPORT_WIDGET_SHAPE_FRAGMENT,
  ROLE_FRAGMENT,
  RULE_DEFINITION_SUGGESTION_CATEGORIZE_FRAGMENT,
  RULE_DEFINITION_SUGGESTION_DUPLICATE_FRAGMENT,
  RULE_DEFINITION_SUGGESTION_MATCH_DOCUMENT_FRAGMENT,
  RULE_DEFINITION_SUGGESTION_SPLIT_FRAGMENT,
  RULE_DEFINITION_SUGGESTION_TRANSFER_FRAGMENT,
  RULE_FRAGMENT,
  RULE_SUGGESTION_LINE_FRAGMENT,
  SEARCH_RESULT_FRAGMENT,
  STRIPE_FINANCIAL_ACCOUNT_FRAGMENT,
  TAG_FRAGMENT,
  TAG_GROUP_FRAGMENT,
  TAX_FRAGMENT,
  TAX_RATE_FRAGMENT,
  THREAD_FRAGMENT,
  THREAD_LIST_FRAGMENT,
  THREAD_MESSAGE_FRAGMENT,
  TRANSACTION_FRAGMENT,
  TRIAGE_FRAGMENT,
  TRIAGE_LIST_ITEM_FRAGMENT,
  TRIAGE_SUGGESTION_FRAGMENT,
  USER_FRAGMENT,
  USER_PROFILE_FRAGMENT,
  USER_SETTINGS_FRAGMENT,
  VIEW_OBJECT_FRAGMENT,
]

export { Fragments };
